import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useSiteConfig } from '../../context/SiteCustomizationContext';
import apiService from '../../services/apiService';
import { 
  Palette, Type, Save, Upload, Trash2, Plus, Edit2, RefreshCw, Home, FileText, MessageSquare, Settings, CheckCircle2, User, Search, Calculator, X, Star
} from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { get, set } from 'lodash';

// Main form data structure that matches the KV store
interface SiteCustomizationData {
  identity: { siteName?: string; logoUrl?: string; faviconUrl?: string };
  colors: { primary?: string; secondary?: string };
  home: {
    hero?: { title?: string; subtitle?: string; ctaText?: string; ctaLink?: string; imageUrl?: string };
    featuredServices?: Array<{ id: string; title: string; description: string; imageUrl: string; link?: string }>;
    testimonials?: Array<{ id: string; name: string; role: string; quote: string; rating: number }>;
  };
  about: { title?: string; content?: string; imageUrl?: string };
  contact: { email?: string; phone?: string; address?: { street?: string; city?: string; postalCode?: string; country?: string } };
  seo: { metaTitle?: string; metaDescription?: string; ogImageUrl?: string };
  // Add other sections as needed
}

const SiteCustomization: React.FC = () => {
  const {
    allConfig,
    loading: contextLoading,
    error: contextError,
    fetchAllConfig,
    updateConfig,
    deleteEntity,
    reload,
  } = useSiteConfig();

  const [activeTab, setActiveTab] = useState('general');
  const [isSaving, setIsSaving] = useState(false);

  const { register, handleSubmit, reset, control, watch, setValue } = useForm<SiteCustomizationData>();

  // Load initial data from context
  useEffect(() => {
    if (!allConfig) {
      fetchAllConfig();
    }
  }, [allConfig, fetchAllConfig]);

  // Populate form when data is loaded or changed
  useEffect(() => {
    if (allConfig) {
      reset(allConfig as any);
    }
  }, [allConfig, reset]);

  const handleSave = async (data: SiteCustomizationData) => {
    setIsSaving(true);
    try {
      // Deep clone the data to avoid modifying the original form data
      let dataToSend = JSON.parse(JSON.stringify(data));

      // Function to recursively clean null/undefined string values to empty strings
      const cleanData = (obj: any) => {
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            if (typeof obj[key] === 'string' && (obj[key] === null || obj[key] === undefined)) {
              obj[key] = '';
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
              cleanData(obj[key]);
            }
          }
        }
      };

      cleanData(dataToSend); // Apply cleaning

      // Preprocess featuredServices (existing logic)
      if (dataToSend.home?.featuredServices) {
        dataToSend.home.featuredServices = dataToSend.home.featuredServices.map(
          (service: any) => {
            if (service.id && service.id.startsWith('new_')) {
              const { id, ...rest } = service;
              return rest;
            }
            return service;
          }
        );
      }

      // Preprocess testimonials (existing logic)
      if (dataToSend.home?.testimonials) {
        dataToSend.home.testimonials = dataToSend.home.testimonials.map(
          (testimonial: any) => {
            if (testimonial.id && testimonial.id.startsWith('new_')) {
              const { id, ...rest } = testimonial;
              return rest;
            }
            return testimonial;
          }
        );
      }

      await updateConfig(dataToSend);
      toast.success('Configuration mise à jour avec succès !');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la mise à jour.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = useCallback(async (files: File[], fieldName: string) => {
    if (files.length === 0) return;
    const toastId = toast.loading('Téléchargement de l\'image...');
    try {
      const formData = new FormData();
      formData.append('image', files[0]);
      //const response = await apiService.siteCustomization.uploadImage(formData);
      /*if (response.data.success) {
        const imageUrl = response.data.data.url;
        setValue(fieldName as any, imageUrl, { shouldDirty: true });
        toast.success('Image téléchargée !', { id: toastId });
      } else {
        throw new Error(response.data.message);
      }*/
    } catch (error: any) {
      toast.error(error.message || 'Échec du téléchargement.', { id: toastId });
    }
  }, [setValue]);

  const handleDeleteEntity = async (key: string, id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      const toastId = toast.loading('Suppression en cours...');
      try {
        await deleteEntity(key, { id });
        toast.success('Élément supprimé.', { id: toastId });
      } catch (error: any) {
        toast.error(error.message || 'Échec de la suppression.', { id: toastId });
      }
    }
  };

  const { fields: services, append: appendService, remove: removeService } = useFieldArray({ control, name: "home.featuredServices" as const });
  const { fields: testimonials, append: appendTestimonial, remove: removeTestimonial } = useFieldArray({ control, name: "home.testimonials" as const });

  const tabs = [
    { id: 'general', label: 'Général', icon: Settings },
    { id: 'home', label: 'Page d\'accueil', icon: Home },
    { id: 'about', label: 'À propos', icon: User },
    { id: 'contact', label: 'Contact', icon: MessageSquare },
    { id: 'seo', label: 'SEO', icon: Search },
  ];

  if (contextLoading && !allConfig) {
    return <Layout><div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Palette className="w-8 h-8 mr-3 text-primary-600" />
              Personnalisation du Site
            </h1>
            <p className="text-gray-600 mt-2">Gérez l\'apparence et le contenu du site web public</p>
          </div>
          <div className="flex space-x-4">
            <Button variant="ghost" onClick={reload}><RefreshCw className="w-4 h-4 mr-2" />Recharger</Button>
            <Button onClick={handleSubmit(handleSave)} isLoading={isSaving}><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </div>
        </div>

        <Card className="p-1"><div className="flex flex-wrap gap-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
              <tab.icon className="w-4 h-4" /><span>{tab.label}</span>
            </button>
          ))}
        </div></Card>

        <form onSubmit={handleSubmit(handleSave)}>
          {activeTab === 'general' && (
            <Card className="p-6"><h3 className="text-lg font-semibold text-gray-900 mb-6">Identité et Couleurs</h3><div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField label="Nom du site" {...register('identity.siteName')} />
              <ImageUploadField label="Logo" fieldName="identity.logoUrl" value={watch('identity.logoUrl')} onUpload={handleImageUpload} setValue={setValue} />
              <ColorField label="Couleur Primaire" {...register('colors.primary')} />
              <ColorField label="Couleur Secondaire" {...register('colors.secondary')} />
            </div></Card>
          )}

          {activeTab === 'home' && (
            <div className="space-y-6">
              <Card className="p-6"><h3 className="text-lg font-semibold text-gray-900 mb-6">Section Hero</h3><div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="Titre" {...register('home.hero.title')} />
                <InputField label="Sous-titre" {...register('home.hero.subtitle')} />
                <InputField label="Texte du bouton" {...register('home.hero.ctaText')} />
                <InputField label="Lien du bouton" {...register('home.hero.ctaLink')} />
                <ImageUploadField label="Image de fond" fieldName="home.hero.imageUrl" value={watch('home.hero.imageUrl')} onUpload={handleImageUpload} setValue={setValue} />
              </div></Card>
              <RepeaterSection title="Services Vedettes" fields={services} onAdd={() => appendService({ id: '', title: '', description: '', imageUrl: '' })} onRemove={handleDeleteEntity} name="home.featuredServices" renderItem={(field, index) => (
                <><InputField label="Titre" {...register(`home.featuredServices.${index}.title`)} /><InputField label="Description" {...register(`home.featuredServices.${index}.description`)} /><ImageUploadField label="Image" fieldName={`home.featuredServices.${index}.imageUrl`} value={field.imageUrl} onUpload={(files) => handleImageUpload(files, `home.featuredServices.${index}.imageUrl`)} setValue={setValue} /></>
              )} />
              <RepeaterSection title="Témoignages" fields={testimonials} onAdd={() => appendTestimonial({ id: '', name: '', role: '', quote: '', rating: 5 })} onRemove={handleDeleteEntity} name="home.testimonials" renderItem={(field, index) => (
                <><InputField label="Nom" {...register(`home.testimonials.${index}.name`)} /><InputField label="Rôle/Entreprise" {...register(`home.testimonials.${index}.role`)} /><InputField label="Citation" {...register(`home.testimonials.${index}.quote`)} /></>
              )} />
            </div>
          )}
          
          {activeTab === 'about' && (
             <Card className="p-6"><h3 className="text-lg font-semibold text-gray-900 mb-6">Page À Propos</h3><div className="space-y-4">
                <InputField label="Titre" {...register('about.title')} />
                <TextareaField label="Contenu" {...register('about.content')} rows={5} />
                <ImageUploadField label="Image" fieldName="about.imageUrl" value={watch('about.imageUrl')} onUpload={handleImageUpload} setValue={setValue} />
              </div></Card>
          )}

          {activeTab === 'contact' && (
             <Card className="p-6"><h3 className="text-lg font-semibold text-gray-900 mb-6">Page Contact</h3><div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="Email" {...register('contact.email')} type="email" />
                <InputField label="Téléphone" {...register('contact.phone')} />
                <InputField label="Rue" {...register('contact.address.street')} />
                <InputField label="Ville" {...register('contact.address.city')} />
                <InputField label="Code Postal" {...register('contact.address.postalCode')} />
                <InputField label="Pays" {...register('contact.address.country')} />
              </div></Card>
          )}

          {activeTab === 'seo' && (
             <Card className="p-6"><h3 className="text-lg font-semibold text-gray-900 mb-6">SEO</h3><div className="space-y-4">
                <InputField label="Titre par défaut" {...register('seo.metaTitle')} />
                <TextareaField label="Description par défaut" {...register('seo.metaDescription')} rows={3} />
                <ImageUploadField label="Image Open Graph" fieldName="seo.ogImageUrl" value={watch('seo.ogImageUrl')} onUpload={handleImageUpload} setValue={setValue} />
              </div></Card>
          )}

        </form>
      </div>
    </Layout>
  );
};

// --- Reusable Form Components ---
const InputField = React.forwardRef<HTMLInputElement, { label: string } & React.InputHTMLAttributes<HTMLInputElement>>(({ label, ...props }, ref) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input ref={ref} {...props} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
  </div>
));

const TextareaField = React.forwardRef<HTMLTextAreaElement, { label: string } & React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ label, ...props }, ref) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <textarea ref={ref} {...props} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
  </div>
));

const ColorField = React.forwardRef<HTMLInputElement, { label: string } & React.InputHTMLAttributes<HTMLInputElement>>(({ label, ...props }, ref) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="flex items-center space-x-2">
      <input type="color" {...props} className="w-10 h-10 border-0 p-0" />
      <input type="text" ref={ref} {...props} className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
    </div>
  </div>
));

const ImageUploadField: React.FC<{label: string, fieldName: string, value: string | undefined, onUpload: (files: File[], fieldName: string) => void, setValue: Function}> = ({ label, fieldName, value, onUpload, setValue }) => {
  const { getRootProps, getInputProps } = useDropzone({ onDrop: (files) => onUpload(files, fieldName), accept: { 'image/*': [] }, multiple: false });
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center space-x-4">
        {value && <img src={value} alt={label} className="w-16 h-16 object-contain border rounded" />}
        <div {...getRootProps()} className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-500 flex-1">
          <input {...getInputProps()} />
          <Upload className="w-6 h-6 mx-auto text-gray-400" /><p className="text-sm text-gray-500 mt-1">Cliquer ou glisser</p>
        </div>
      </div>
    </div>
  );
};

const RepeaterSection: React.FC<{title: string, fields: any[], onAdd: () => void, onRemove: (key: string, id: string) => void, name: string, renderItem: (field: any, index: number) => React.ReactNode}> = ({ title, fields, onAdd, onRemove, name, renderItem }) => (
  <Card className="p-6">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <Button onClick={onAdd}><Plus className="w-4 h-4 mr-2" />Ajouter</Button>
    </div>
    <div className="space-y-4">
      {fields.map((field, index) => (
        <div key={field.id} className="border rounded-lg p-4 relative"><div className="space-y-2">
          {renderItem(field, index)}
        </div><Button variant="ghost" size="sm" onClick={() => onRemove(name, field.id)} className="absolute top-2 right-2"><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
      ))}
    </div>
  </Card>
);

export default SiteCustomization;
